import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { useInScreen } from '@client/core/hooks';
import { getAdConfig, isFeatureEnabled } from '@client/core/state/reducer';
import {
  AdServer,
  GamConfig,
  PlacementId,
  Slot,
  debugLog,
  GamPlacement,
  UNLEASH_FEATURE_NAME
} from '@schibsted-nmp/advertising-shared';

type Props = {
  containerId: string;
  placementId: PlacementId;
  ref: RefObject<HTMLDivElement>;
  forecasting?: boolean;
};
type GamRequestStatus = 'pending' | 'loaded' | 'requested' | 'error';

export function useInitiateGamUnit(props: Props) {
  const { placementId } = props;
  const shouldLoadAd = useShouldLoadAd(props);
  const [status, setStatus] = useState<GamRequestStatus>('pending');

  const definedSlot = useRef<Slot | null>(null);

  const { gamConfig, placement } = useMemo<{
    gamConfig: GamConfig;
    placement?: GamPlacement;
  }>(() => {
    const config = getAdConfig();
    const placements =
      props.forecasting &&
      isFeatureEnabled(UNLEASH_FEATURE_NAME.enableGamForecasting)
        ? config?.forecastingPlacements
        : config?.placements;
    const placement = placements?.find(
      (p) => p.placementId === placementId && p.adServer.type === AdServer.GAM
    ) as GamPlacement | undefined;
    const gamConfig = config?.adServer.gam as GamConfig;
    return { gamConfig, placement };
  }, [placementId]);

  /**
   * Load the ad when ready
   */
  useEffect(() => {
    if (shouldLoadAd && status !== 'loaded' && definedSlot.current) {
      setStatus('loaded');
      if (placement) {
        window.googletag.cmd.push(() => {
          window.googletag.pubads().refresh([definedSlot.current]);
        });
      }
    }
  }, [shouldLoadAd, status, placement, definedSlot.current]);

  /**
   * Set up the ad
   */
  useEffect(() => {
    window.googletag = window.googletag || {};
    window.googletag.cmd = window.googletag.cmd || [];

    if (placement) {
      window.googletag.cmd.push(() => {
        definedSlot.current = window.googletag
          .defineSlot(
            placement.adServer.config.path,
            placement.adServer.config.sizes,
            placement.placementId
          )
          .addService(window.googletag.pubads());

        // Set targeting for the current slot
        if (gamConfig?.targeting) {
          gamConfig?.targeting.forEach(({ key, value }) => {
            if (key && value) {
              definedSlot.current?.setTargeting(key, value);
            }
          });
        }

        if (
          isFeatureEnabled(UNLEASH_FEATURE_NAME.enableGamForecasting) &&
          props.forecasting
        ) {
          definedSlot.current?.setTargeting('forecasting', 'true');
        }
        if (isFeatureEnabled(UNLEASH_FEATURE_NAME.enableGamTestCampaign)) {
          definedSlot.current?.setTargeting('gamTestCampaign', 'true');
        }

        debugLog('definedSlot.current after assignment:', definedSlot.current);

        // Add event listeners for ad events
        window.googletag.pubads().addEventListener('slotRequested', (event) => {
          debugLog('slotRequested event:', event);
        });

        window.googletag
          .pubads()
          .addEventListener('slotRenderEnded', (event) => {
            debugLog('slotRenderEnded event:', event);
          });

        window.googletag
          .pubads()
          .addEventListener('impressionViewable', (event) => {
            debugLog('impressionViewable event:', event);
          });

        window.googletag
          .pubads()
          .addEventListener('slotVisibilityChanged', (event) => {
            debugLog('slotVisibilityChanged event:', event);
          });

        window.googletag.pubads().addEventListener('slotOnload', (event) => {
          debugLog('slotOnload event:', event);
        });
      });
    } else {
      console.error(`No placement found for container: ${placementId}`);
    }
  }, [placement, gamConfig, placementId]);

  return { placement, status };
}

function getInScreenData(
  placementId: PlacementId
): { threshold: number; rootMargin: string } | null {
  switch (placementId) {
    case PlacementId.Right1:
      return null;
    case PlacementId.Top1:
      return {
        threshold: 0,
        rootMargin: '200px 0px 0px 0px'
      };
    default:
      return {
        threshold: 0,
        rootMargin: '100px 0px 0px 0px'
      };
  }
}

function useShouldLoadAd({ placementId, ref }: Props) {
  const inScreenData = useMemo(
    () => getInScreenData(placementId),
    [placementId]
  );

  const { hasIntersected } = useInScreen({ ref, ...(inScreenData || {}) });

  return !inScreenData || hasIntersected;
}
